import { Injectable, inject } from '@angular/core';
import { Events } from '@app/core/models/event-map';
import { PublishSubscribeService } from '@app/core/services/publish-subscribe.service';
import { UserContextSelectors } from '@app/shared/state/user-context/user-context.selectors';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { NotificationsService } from './notifications-internal.service';

@Injectable({
    providedIn: 'root'
})
export class AppService {

    #subscriptions: Subscription[] = []
    #publishSubscribeService = inject(PublishSubscribeService)
    private readonly notificationsService = inject(NotificationsService)
    private readonly store = inject(Store)

    itemsPageSize: number = 10
    figureConfigClosed_HasConfigChanged: boolean = false
    figureConfigClosed_HasConfigNotChanged: boolean = true
    bookingGuid: string
    openWebQuotation: boolean = false
    workOrders: any = {}
    montageAdmin: any = {}
    aftermarketComponentRoot: any = {}
    endCustomer_zipcodeUpdated: boolean = false
    isAuthenticated = this.store.selectSignal(UserContextSelectors.isAuthenticated)
    isAssembler = this.store.selectSignal(UserContextSelectors.isAssembler)
    isAftermarket = this.store.selectSignal(UserContextSelectors.isAftermarket)
    isMf = this.store.selectSignal(UserContextSelectors.isMf)
    showWithVATValue = this.store.selectSignal(UserContextSelectors.showVat)
    _showMeasurements = this.store.selectSignal(UserContextSelectors.showMeasurements)
    _showUValue = this.store.selectSignal(UserContextSelectors.showUValue)

    constructor() {
        this.init()
    }

    init() {
      this.registerEventSubscriptions()
    }

    private registerEventSubscriptions() {

        /** USER_LOGGED_IN */
        this.#subscriptions.push(
            this.#publishSubscribeService.on(Events.UserService.USER_LOGGED_IN)
                .subscribe({
                    next: () => {
                        this.handleEvent_UserService_USER_LOGGED_IN()
                    }
                })
        )

        /** IS_DOING_MESSAGE event*/
        this.#subscriptions.push(
            this.#publishSubscribeService.on(Events.UserService.RESELLER_TYPE_CHANGED)
                .subscribe({
                    next: () => {
                        this.handleEvent_UserService_RESELLER_TYPE_CHANGED()
                    }
                })
        )

    }

    calcPrice = (priceWithoutVAT: number): number => {
        return priceWithoutVAT * (this.showWithVATValue() ? 1.25 : 1.0);
    }

    /** Return price excluding VAT. Used when editing amounts that need to be stored as excl VAT.*/
    calcPriceExclVAT = (price: number): number => {
        return price / (this.showWithVATValue() ? 1.25 : 1.0);
    }

    calcVAT = (priceWithoutVAT: number): number => {
        return this.calcPrice(priceWithoutVAT) - priceWithoutVAT;
    }

    handleEvent_UserService_USER_LOGGED_IN = () => {
        this.doOnLogin();
    }

    handleEvent_UserService_RESELLER_TYPE_CHANGED = () => {
        //Don't load chat for aftermarket and MF users
        if (!this.isAftermarket() && !this.isMf() && this.isAuthenticated()) {
            if (environment.enableTrioChat) {
                this.trioChatLoadScript('https://account.psplugin.com', '4D175724-8BEB-440B-97A4-1A9080FC65AC');
            }
        } else {
            const chatEl: Element = document.querySelector('div[id^="vngage_banner_"]')
            if (chatEl) {
                chatEl.parentNode.removeChild(chatEl);
            }
        }
    }

    trioChatLoadScript = (server: string, psID: string): void => {
        const s: HTMLScriptElement = document.createElement('script');
        s.type = 'text/javascript';
        s.src = server + '/' + psID + '/ps.js';
        document.getElementsByTagName('head')[0].appendChild(s);
    }

    /** Do on user login */
    doOnLogin = (): void => {

        this.#publishSubscribeService.trigger(Events.ActionsService.UPDATE_NEEDED_ACTION_COUNT)

        // Notifications
        // Check if we have a new version notification that the user haven't yet seen
        this.notificationsService.checkPortalNotification_LatestNewVersion();
    }

    Guid = (): string => {
        const crypto = window.crypto || null;

        const _padLeft = (paddingString, width, replacementChar) => {
            return paddingString.length >= width ? paddingString : _padLeft(replacementChar + paddingString, width, replacementChar || ' ');
        };

        const _s4 = (number) => {
            const hexadecimalResult = number.toString(16);
            return _padLeft(hexadecimalResult, 4, '0');
        };

        const _cryptoGuid = () => {
            const buffer = new window.Uint16Array(8);
            crypto.getRandomValues(buffer);
            return [_s4(buffer[0]) + _s4(buffer[1]), _s4(buffer[2]), _s4(buffer[3]), _s4(buffer[4]), _s4(buffer[5]) + _s4(buffer[6]) + _s4(buffer[7])].join('-');
        };

        const _guid = () => {
            let currentDateMilliseconds = new Date().getTime();
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (currentChar) {
                const randomChar = (currentDateMilliseconds + Math.random() * 16) % 16 | 0;
                currentDateMilliseconds = Math.floor(currentDateMilliseconds / 16);
                return (currentChar === 'x' ? randomChar : (randomChar & 0x7 | 0x8)).toString(16);
            });
        };

        const create = () => {
            const hasCrypto = crypto !== undefined && crypto !== null,
                hasRandomValues = hasCrypto && typeof (crypto.getRandomValues) !== 'undefined';
            return (hasCrypto && hasRandomValues) ? _cryptoGuid() : _guid();
        };

        return create()
    }
}

